// Componente per il modello 3D
import { useMemo, useRef, useEffect } from "react";
import { useGLTF, useAnimations } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import {
    Mesh, 
    SkinnedMesh, 
    Bone, 
    LoopOnce, 
    CustomBlending,
    OneFactor,
    Box3,
    Vector3,
} from "three";


export default function ModelLight({
    url,                     // URL del modello 3D
    onError,                 // Callback per gestire gli errori
    // handleHasAnimations,     // Callback per gestire le animazioni
    // isAnimatedOn,            // Flag per l'animazione attiva
    // isFirstAnimation,        // Flag per la prima animazione
    setModelInfo,             // Callback per settare le info del modello
}) {
    // useRef della mappa per salvare i materiali migliorati per la visualizzazione
    const adjustedMaterialsMap = useRef(new Map());
    
    // Ref per il modello da animare
    const modelRef = useRef();

    // Carica il modello 3D
    const { scene, animations } = useGLTF(url, undefined, (error) => {
        console.error('Error loading the model:', error);
        onError(error.message);
    });

    // Assegna un nome unico al modello per facilitare la sua identificazione
    if (scene.name === '' || scene.name === 'AuxScene') {
        scene.name = 'Scene';
    }
    if (animations) {
        scene.animations = animations;
    }

    // Effetto per calcolare e settare le informazioni del modello (dimensioni e centro)
    useEffect(() => {
        if (!setModelInfo) return;

        if (scene) {
            const boundingBox = new Box3().setFromObject(scene);
            const bbSize = boundingBox.getSize(new Vector3());
            const bbCenter = boundingBox.getCenter(new Vector3());

            setModelInfo({
                sizes: { x: bbSize.x, y: bbSize.y, z: bbSize.z },
                center: { x: bbCenter.x, y: bbCenter.y, z: bbCenter.z }
            });

            if (!scene.userData.sceneReadySet) {
                window.isSceneReady = true;
                scene.userData.sceneReadySet = true;
            }
        }
    }, [scene, setModelInfo]);

    // Gestione delle animazioni
    // const { actions, names } = useAnimations(animations, scene);

    // // Configurazione iniziale delle animazioni
    // useEffect(() => {
    //     if (animations && names.length > 0) {
    //         const action = actions[names[0]];

    //         // Configura l'animazione per fermarsi all'ultimo frame
    //         action.clampWhenFinished = true;
    //         action.loop = LoopOnce;
    //         if (names[0].includes('explosion_') && action.getClip().duration > 5) {
    //             // console.log('clip duration before setDuration', action.getClip().duration);
    //             action.getClip().duration = action.getClip().duration * 0.5;
    //             // console.log('clip duration after setDuration', action.getClip().duration);
    //         }

    //         // Notifica il parent che abbiamo un'animazione
    //         handleHasAnimations(true, names[0], action.getClip().duration);

    //         // Metti in pausa l'animazione al primo frame
    //         action.play();
    //         action.paused = true;

    //         // Disabilita il culling per le mesh skinned, per evitare il non rendering delle mesh aninate
    //         scene.traverse((child) => {                 
    //             if (child.isSkinnedMesh) {
    //                 child.frustumCulled = false;
    //             }
    //         });
    //     }
    // }, [url, animations]);

    // // Gestione dell'animazione
    // useEffect(() => {
    //     const action = actions[names[0]];
    //     if (action) {
    //         if (isAnimatedOn) {
    //             if (isFirstAnimation) {
    //                 action.timeScale = 1;
    //                 action.reset();
    //             } else {
    //                 action.timeScale = -1;
    //             }
    //             // console.log(action.time);
    //             action.paused = false;
    //             action.play();
    //         } else {
    //             // console.log(action.time);
    //             action.paused = true;
    //         }
    //     }
    // }, [isAnimatedOn, isFirstAnimation]);

    // Creazione del dizionario dei materiali
    const materialsMap = useMemo(() => {
        const map = new Map();

        scene.traverse((child) => {
            if (child instanceof Bone) return;

            if (child instanceof Mesh || child instanceof SkinnedMesh) {
                if (child.material && !map.has(child.material.name)) {
                    const matClone = child.material.clone();
                    // Rifiniture al materiale se è metallo o trasmissivo 
                    adjustMaterials(matClone);
                    // Aggiunge il materiale alla mappa
                    map.set(child.material.name, matClone);
                }
                child.material = map.get(child.material.name);
            }
        });

        return map;
    }, [scene]);

    // Funzione per regolare i materiali metallici e trasmissivi 
    function adjustMaterials(material) {
        let materialHasChanged = false;
        // Caso materiale metallico
        if (material.name.startsWith('MT_') && material.emissiveIntensity > 0) {
            material.emissiveIntensity = 0;
            materialHasChanged = true;
        }

        // Caso materiale trasmissivo
        if (material.name.startsWith('TR_')) {
            material.transparent = true;
            material.blending = CustomBlending;
            material.blendSrc = OneFactor;
            if (material.opacity < 1) {
                material.opacity = 1;
            }
            materialHasChanged = true;
        }

        // Caso tutti i materiali non lenti che hanno la sheen
        if (!material.name.startsWith('LN_')) {
            if (material.sheen > 0.5) {
                material.sheen = 0.5;
                materialHasChanged = true;
            }
        } else {
            if (!material.name.includes('_TRS_') && !material.depthWrite) {
                material.depthWrite = true;
                materialHasChanged = true;
            }
        }
        if (materialHasChanged && !adjustedMaterialsMap.current.has(material.name)) {
            adjustedMaterialsMap.current.set(material.name, material);
        }
    }

    return (
        <>
            {/* Renderizza il modello 3D */}
            <primitive ref={modelRef} object={scene}/>    
        </>
    );
};